import React, { ChangeEventHandler, useRef } from 'react';
import styled from '@xstyled/styled-components';
import { useToggleAudioEffect } from '../audio-effect/useToggleAudioEffect';

const Input = styled.input`
  width: 100%;
  height: 100%;
  opacity: 1;
  z-index: -1;
`;

const Label = styled.label`
  position: relative;
  display: inline-block;
  font-size: ${p => p.theme.space.sm};
  width: 6em;
  height: 3.4em;
`;

const Slider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  box-sizing: border-box;
  background-color: ${p => p.theme.colors.greyDark};
  border: 1px solid transparent;
  -webkit-transition: 0.4s;
  transition: 0.4s;
  border-radius: 3.4em;

  &::before {
    position: absolute;
    content: '';
    height: 2.6em;
    width: 2.6em;
    left: 0.4em;
    bottom: 0.4em;
    background-color: white;
    -webkit-transition: 0.4s;
    transition: 0.4s;
    border-radius: 50%;
  }

  ${Input}:checked + & {
    background-color: ${p => p.theme.colors.accentDark};
    /* Near-white brand → visible edge (same token as Play / primary buttons). */
    border-color: var(--color-accent-edge);
  }

  ${Input}:checked + &::before {
    -webkit-transform: translateX(2.6em);
    -ms-transform: translateX(2.6em);
    transform: translateX(2.6em);
    /* White brand → black knob; dark brand → white knob. */
    background-color: var(--color-on-accent);
  }

  ${Input}:disabled + & {
    pointer-events: none;
    background: #e6e6e6;
  }
`;

interface Props {
  id: string;
  name: string;
  value: string;
  checked: boolean | undefined;
  disabled: boolean | undefined;
  showLabel: boolean | undefined;
  onChange: ChangeEventHandler<HTMLInputElement>;
  /** CAT-1074: purpose-specific name when the visible label is shared/ambiguous. */
  'aria-label'?: string;
}

export const ToggleSwitch = ({
  id,
  name,
  value,
  checked,
  onChange,
  disabled,
  showLabel = true,
  'aria-label': ariaLabel
}: Props) => {
  const [playAudioEffect] = useToggleAudioEffect();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleLabelClick = () => {
    if (inputRef.current) {
      inputRef.current.checked = !inputRef.current.checked;
      try {
        playAudioEffect();
      } catch (error) {
        // Silently ignore audio effect errors
      }
      onChange({
        target: inputRef.current
      } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  if (showLabel) {
    return (
      <Label htmlFor={id}>
        <Input
          id={id}
          type="checkbox"
          name={name}
          value={value}
          disabled={disabled}
          checked={checked}
          aria-label={ariaLabel}
          onChange={e => {
            try {
              playAudioEffect();
            } catch (error) {
              // Silently ignore audio effect errors
            }
            onChange(e);
          }}
        />
        <Slider aria-hidden="true" />
      </Label>
    );
  } else {
    return (
      <Label as="span" onClick={handleLabelClick}>
        <Input
          id={id}
          type="checkbox"
          name={name}
          value={value}
          disabled={disabled}
          checked={checked}
          aria-label={ariaLabel}
          onChange={e => {
            try {
              playAudioEffect();
            } catch (error) {
              // Silently ignore audio effect errors
            }
            onChange(e);
          }}
          ref={inputRef}
        />
        <Slider aria-hidden="true" />
      </Label>
    );
  }
};

ToggleSwitch.defaultProps = {
  disabled: false,
  checked: false
};
